/**
 * Krok VYV1 — TISK NABIDKY: vyloha do vylohy, letak s QR a list do sanonu.
 *
 * Z prohlidky u maklerky MV reality (29. 10. 2026, bod 2): z Realmana tiskne
 * dve PDF — vyvesku do vylohy BEZ svych udaju a list do sanonu S kontaktem na
 * majitele — a z patnacti variant vybira „nejmene spatnou". Tenhle modul dela
 * tri PREDVOLBY (`LISTING_PRINT_PRESETS`) a u kazde se da zaskrtnout, co na
 * papire je (`LISTING_PRINT_PARTS`).
 *
 * JEDNA SABLONA, TRI PREDVOLBY: skladba, typografie i tiskove CSS jsou
 * spolecne (tataz uvaha jako u brandu, `brand.ts`) — predvolba meni jen to,
 * KTERE bloky se vykresli a jak velke je hlavni foto. Tri sablony by se casem
 * rozesly.
 *
 * VYPNUTY BLOK SE NEVYKRESLI, nejen schova: `parts.broker === false` znamena,
 * ze v HTML nikde nejsou udaje maklere (meri to `nabidka-tisk.spec.ts`).
 * Papir, ze ktereho jde CTRL+F najit kontakt, by vytku „nepotrebuji mit udaje
 * o sobe" neresil.
 *
 * DATA JSOU UZ HOTOVY TEXT (`rows`, `priceText`): dokument je PREVOD KARTY
 * NEMOVITOSTI, ne druhy vypocet nad fakty — tataz uvaha a tyz tvar vstupu jako
 * u prehledu parcely (`parcel-overview.ts`, krok MAP3b). Druhy formatovac
 * hodnot by se s obrazovkou rozesel (CLAUDE.md §8 bod 2).
 *
 * BEZPECNOST: vsechny texty jsou DATA, nikdy markup (`esc`), a obrazky se
 * prijimaji JEN jako `data:` URI (PNG/JPEG, u QR i SVG) — zadna `https://`
 * adresa, aby Gotenberg (chromium) nestahoval nic z ciziho hosta (tataz
 * zabrana jako u `parcel-overview.ts` a `decodeBrandLogo`).
 */
import { t } from '@reos/i18n';
import { esc } from '../html.js';
/** Predvolby z zadani: vyloha (bez maklere), letak s QR, list do sanonu. */
export const LISTING_PRINT_PRESETS = ['vyloha', 'letak-qr', 'sanon'];
/** Zaskrtavatka „co se tiskne" (zadani bod 2). Fotky maji vlastni POCET. */
export const LISTING_PRINT_PARTS = ['broker', 'owner', 'price', 'params', 'description', 'map', 'qr'];
export const LISTING_PRINT_ORIENTATIONS = ['portrait', 'landscape'];
/** Strop na pocet fotek — vic se na jednu stranu A4 citelne nevejde. */
export const LISTING_PRINT_MAX_PHOTOS = 6;
function parts(on) {
    return Object.fromEntries(LISTING_PRINT_PARTS.map((part) => [part, on.includes(part)]));
}
/**
 * Vychozi stav predvoleb PRESNE podle zadani bodu 1:
 *  - Vyloha: velke foto, cena, hlavni parametry, logo kancelare, BEZ maklere,
 *  - Letak s QR: totez + popis, fotky a QR na verejnou stranku nemovitosti,
 *  - Sanon: vse vcetne kontaktu na majitele a maklere (interni list).
 */
export const LISTING_PRINT_DEFAULTS = {
    vyloha: {
        preset: 'vyloha',
        orientation: 'portrait',
        parts: parts(['price', 'params']),
        photoCount: 1,
    },
    'letak-qr': {
        preset: 'letak-qr',
        orientation: 'portrait',
        parts: parts(['price', 'params', 'description', 'qr', 'broker']),
        photoCount: 3,
    },
    /*
     * Sanon = INTERNI list: vse, co o nabidce vime, vcetne kontaktu na
     * majitele. Mapa je VYPNUTA i tady: dokument ji umi (`parts.map`), ale
     * vyrez do nej musi nekdo dodat — dokud administrace nema odkud (licence
     * Mapy.com zakazuje ukladat vystup, vyrezy `dd_maps` ma jen provereni),
     * byl by zapnuty prepinac slib, ktery papir nesplni.
     */
    sanon: {
        preset: 'sanon',
        orientation: 'portrait',
        parts: parts(['price', 'params', 'description', 'qr', 'broker', 'owner']),
        photoCount: 4,
    },
};
export function isListingPrintPreset(value) {
    return typeof value === 'string' && LISTING_PRINT_PRESETS.includes(value);
}
function clampPhotoCount(value, fallback) {
    if (value === undefined || !Number.isFinite(value))
        return fallback;
    return Math.min(LISTING_PRINT_MAX_PHOTOS, Math.max(0, Math.trunc(value)));
}
/**
 * Vysledna volba = vychozi predvolba <- nastaveni kancelare <- volba maklere
 * (pozdejsi vrstva prebiji). Patch nese jen prepsane klice, takze nova cast
 * dokumentu se k maklerovi, ktery si uz nekdy volbu ulozil, dostane
 * s vychozim stavem — ne vypnuta.
 */
export function resolveListingPrintOptions(preset, ...layers) {
    let out = LISTING_PRINT_DEFAULTS[preset];
    for (const layer of layers) {
        const patch = layer?.[preset];
        if (patch === undefined)
            continue;
        out = {
            preset,
            orientation: patch.orientation ?? out.orientation,
            parts: { ...out.parts, ...(patch.parts ?? {}) },
            photoCount: clampPhotoCount(patch.photoCount, out.photoCount),
        };
    }
    return { ...out, photoCount: clampPhotoCount(out.photoCount, LISTING_PRINT_DEFAULTS[preset].photoCount) };
}
const RASTER_DATA_URL = /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/=]+$/u;
const QR_DATA_URL = /^data:image\/(?:png|svg\+xml);base64,[A-Za-z0-9+/=]+$/u;
/** Strop na jeden vlozeny obrazek — PDF je podklad do vylohy, ne archiv rastru. */
export const LISTING_PRINT_MAX_IMAGE_BYTES = 6 * 1024 * 1024;
export function safeListingPrintImage(value, pattern = RASTER_DATA_URL) {
    if (typeof value !== 'string' || !pattern.test(value))
        return null;
    const base64 = value.slice(value.indexOf(',') + 1);
    // `base64` je 4/3 bajtu dat; delku meri na ZNACICH, aby se nic nedekodovalo.
    return (base64.length * 3) / 4 > LISTING_PRINT_MAX_IMAGE_BYTES ? null : value;
}
export function safeListingPrintQr(value) {
    return safeListingPrintImage(value, QR_DATA_URL);
}
/** Nazev souboru ke stazeni (bez diakritiky a mezer). */
export function listingPrintFilename(preset) {
    return `nabidka-${preset}.pdf`;
}
function czDateOf(iso) {
    const [y, m, d] = iso.split('-');
    return `${String(Number(d))}. ${String(Number(m))}. ${y ?? ''}`;
}
function key(path) {
    return `listing.print.${path}`;
}
function contactBlock(heading, contact, note) {
    const lines = [contact.name, contact.phone, contact.email]
        .map((line) => (line ?? '').trim())
        .filter((line) => line !== '')
        .map((line) => `<span>${esc(line)}</span>`)
        .join('');
    if (lines === '')
        return '';
    return `<div class="contact"><h3>${esc(heading)}</h3><p>${lines}</p>${note === null ? '' : `<p class="internal">${esc(note)}</p>`}</div>`;
}
/**
 * Sazba jedne strany A4 (na vysku i na sirku) pro jednu predvolbu.
 *
 * Rozmer strany urcuje CSS (`@page size`), takze volba „na sirku" plati i v
 * Gotenbergu bez dalsiho pole v pozadavku — PDF cesta ji pousti pres
 * `preferCssPageSize` (`fullPage`, viz `gotenberg.ts`).
 */
export function renderListingPrintHtml(input, options) {
    const locale = input.locale ?? 'cs';
    const tr = (path, values) => t(key(path), { locale, ...(values === undefined ? {} : { values }) });
    const show = options.parts;
    const photos = input.photos
        .map((photo) => safeListingPrintImage(photo))
        .filter((photo) => photo !== null)
        .slice(0, clampPhotoCount(options.photoCount, 0));
    const hero = photos[0] ?? null;
    const rest = photos.slice(1);
    const qr = show.qr ? safeListingPrintQr(input.qrDataUrl) : null;
    const mapImage = show.map ? safeListingPrintImage(input.mapImageDataUrl) : null;
    const logo = safeListingPrintImage(input.officeLogoDataUrl);
    const rows = show.params
        ? input.rows
            .filter((row) => row.value.trim() !== '')
            .map((row) => `<tr><th scope="row">${esc(row.label)}</th><td>${esc(row.value)}</td></tr>`)
            .join('\n')
        : '';
    const brandHead = [
        logo === null ? '' : `<img class="logo" src="${logo}" alt="${esc(input.officeName ?? '')}" />`,
        input.officeName === null || input.officeName.trim() === ''
            ? ''
            : `<span class="office">${esc(input.officeName)}</span>`,
    ].join('');
    const contacts = [
        show.broker && input.broker !== null ? contactBlock(tr('brokerHeading'), input.broker, null) : '',
        show.owner && input.owner !== null ? contactBlock(tr('ownerHeading'), input.owner, tr('internalNote')) : '',
    ].join('');
    const footerLines = [
        input.officeFooterText,
        input.officeContactLine,
        tr('generatedOn', { date: czDateOf(input.generatedOn) }),
    ]
        .map((line) => (line ?? '').trim())
        .filter((line) => line !== '')
        .map((line) => `<span>${esc(line)}</span>`)
        .join('');
    return `<!doctype html>
<html lang="${esc(locale)}"><head><meta charset="utf-8" />
<title>${esc(input.title)}</title>
<style>
  @page { size: A4 ${options.orientation}; margin: 12mm 12mm 10mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, 'Helvetica Neue', sans-serif; font-size: 10.5pt; color: #1d2430; line-height: 1.4; margin: 0; }
  header { display: flex; align-items: center; justify-content: space-between; gap: 10mm; border-bottom: 1px solid #d9dde3; padding-bottom: 4mm; margin-bottom: 5mm; }
  .logo { max-height: 14mm; max-width: 50mm; }
  .office { font-size: 11pt; font-weight: 600; color: #2b3442; }
  h1 { font-size: 20pt; margin: 0 0 1mm; line-height: 1.15; }
  .addr { color: #5f6570; margin: 0 0 4mm; font-size: 11pt; }
  .price { font-size: 24pt; font-weight: 700; margin: 0 0 4mm; }
  .hero { margin: 0 0 3mm; }
  .hero img { width: 100%; max-height: 110mm; object-fit: cover; border-radius: 2mm; display: block; }
  .strip { display: flex; gap: 2mm; margin: 0 0 4mm; }
  .strip img { width: 100%; height: 32mm; object-fit: cover; border-radius: 1.5mm; }
  table { width: 100%; border-collapse: collapse; margin: 0 0 4mm; }
  th, td { text-align: left; vertical-align: top; border-top: 1px solid #d9dde3; padding: 1.6mm 2mm; }
  th { width: 40%; font-weight: 600; color: #2b3442; }
  .desc { margin: 0 0 4mm; white-space: pre-line; }
  .qr { display: flex; align-items: center; gap: 4mm; margin: 0 0 4mm; }
  .qr img { width: 28mm; height: 28mm; }
  .qr .url { font-size: 9pt; color: #5f6570; word-break: break-all; }
  .map img { width: 100%; max-height: 70mm; object-fit: cover; border: 1px solid #d9dde3; border-radius: 1.5mm; display: block; }
  .attribution { color: #5f6570; font-size: 8.5pt; margin: 1mm 0 4mm; }
  .contacts { display: flex; gap: 8mm; border-top: 1px solid #d9dde3; padding-top: 3mm; }
  .contact h3 { font-size: 10pt; margin: 0 0 1mm; color: #2b3442; }
  .contact p { margin: 0; display: flex; flex-direction: column; }
  .internal { color: #8a5a00; font-size: 8.5pt; margin-top: 1mm; }
  footer { margin-top: 5mm; border-top: 1px solid #d9dde3; padding-top: 2mm; color: #5f6570; font-size: 8.5pt; display: flex; justify-content: space-between; gap: 6mm; }
  footer .office-lines { display: flex; flex-direction: column; }
  /* Vyloha se cte z ulice: nazev, cena a foto maximalne velke, nic drobneho. */
  body[data-preset='vyloha'] h1 { font-size: 30pt; }
  body[data-preset='vyloha'] .price { font-size: 40pt; }
  body[data-preset='vyloha'] .addr { font-size: 14pt; }
  body[data-preset='vyloha'] { font-size: 13pt; }
  body[data-preset='vyloha'] .hero img { max-height: 140mm; }
</style></head>
<body data-doc="nabidka-tisk" data-preset="${esc(options.preset)}">
<header><span class="brand">${brandHead}</span><span class="kicker">${esc(tr(`preset.${options.preset}`))}</span></header>
<h1>${esc(input.title)}</h1>
${input.addressText === null || input.addressText.trim() === '' ? '' : `<p class="addr">${esc(input.addressText)}</p>`}
${show.price
        ? `<p class="price">${esc(input.priceText === null || input.priceText.trim() === '' ? tr('priceMissing') : input.priceText)}</p>`
        : ''}
${hero === null ? '' : `<figure class="hero"><img src="${hero}" alt="${esc(input.title)}" /></figure>`}
${rest.length === 0 ? '' : `<div class="strip">${rest.map((photo) => `<img src="${photo}" alt="${esc(input.title)}" />`).join('')}</div>`}
${rows === '' ? '' : `<table>${rows}</table>`}
${show.description && input.description !== null && input.description.trim() !== '' ? `<p class="desc">${esc(input.description.trim())}</p>` : ''}
${qr === null
        ? ''
        : `<div class="qr"><img src="${qr}" alt="${esc(tr('qrAlt'))}" /><span class="url"><strong>${esc(tr('qrHint'))}</strong><br />${esc(input.publicUrl ?? '')}</span></div>`}
${mapImage === null
        ? ''
        : `<figure class="map"><img src="${mapImage}" alt="${esc(tr('mapHeading'))}" /></figure>${input.mapAttribution === null || input.mapAttribution === undefined
            ? ''
            : `<p class="attribution">${esc(input.mapAttribution)}</p>`}`}
${contacts === '' ? '' : `<div class="contacts">${contacts}</div>`}
<footer><span class="office-lines">${footerLines}</span><span class="powered">${esc(t('product.poweredBy', { locale }))}</span></footer>
</body></html>`;
}
/** Text paticky stran PDF (Gotenberg `footerLabel`). */
export function listingPrintFooterLabel(preset, officeName, locale = 'cs') {
    const label = t(key(`preset.${preset}`), { locale });
    return officeName === null || officeName.trim() === ''
        ? label
        : t(key('footer'), { locale, values: { label, office: officeName.trim() } });
}
//# sourceMappingURL=nabidka-tisk.js.map